import { useState } from 'react'
import { router } from '@inertiajs/react'
import { SlidersHorizontal } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import ProactivityInstructionsDialog from '@/frontend/features/Fde/ProactivityInstructionsDialog'
import MeetingPreparationControl from '@/frontend/features/Fde/MeetingPreparationControl'
import type { ProactivityCopy } from '@/frontend/features/Fde/ProjectAgentProactivity'
import type { AgentProactivitySettings, ProjectAgent } from '@/frontend/features/Fde/types'

export default function ProjectAgentProactivityControl({ projectId, assignment, copy, disabled }: {
  projectId: string; assignment: ProjectAgent; copy: ProactivityCopy; disabled: boolean
}) {
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<AgentProactivitySettings | null>(null)
  const settings = assignment.proactivity!
  const controls = [
    { field: 'proactivity_enabled', label: copy.enabled, checked: settings.proactivity_enabled, description: settings.profile.enabled_description },
    ...(settings.source_preparation_configurable ? [{ field: 'source_preparation_enabled', label: copy.source_preparation, checked: settings.source_preparation_enabled, description: copy.source_preparation_description }] : []),
    ...(settings.approval_configurable ? [{ field: 'proactive_work_approval_enabled', label: copy.approval, checked: settings.proactive_work_approval_enabled, description: copy.approval_description }] : []),
  ]
  return <div className="grid gap-5 py-6 sm:grid-cols-[minmax(0,1fr)_2fr]" aria-busy={busy}>
    <div className="min-w-0">
      <p className="truncate text-sm font-medium">{assignment.agent.name}</p>
      <p className="mt-1 text-xs text-muted-foreground">{settings.profile.title}</p>
    </div>
    <div className="space-y-5">
      <p className="text-xs leading-relaxed text-muted-foreground">{settings.profile.description}</p>
      {controls.map(({ field, label, checked, description }) => <label key={field} className="flex items-start gap-2.5 text-sm">
        <Checkbox className="mt-0.5" checked={checked} disabled={disabled || busy} aria-label={`${assignment.agent.name}: ${label}`} onCheckedChange={(value) => {
          setBusy(true)
          router.patch(`/dashboard/discovery/agent_proactivities/${assignment.id}?project_id=${projectId}`, {
            revision: settings.revision, proactivity: { [field]: value === true },
          }, { preserveScroll: true, onFinish: () => setBusy(false) })
        }} />
        <span><span className="leading-5">{label}</span><span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{description}</span></span>
      </label>)}
      {settings.meeting_preparation && <MeetingPreparationControl key={`${assignment.id}:${settings.revision}`} projectId={projectId}
        assignment={assignment} copy={copy} disabled={disabled || busy} />}
      <div className="flex flex-wrap items-center gap-3 border-t border-border/60 pt-3">
        <Button type="button" variant="ghost" size="sm" disabled={disabled || busy} className="-ml-2 h-8 text-xs" onClick={() => setEditing(settings)} aria-label={`${assignment.agent.name}: ${settings.attention_prompt ? copy.attention_title : copy.customize}`}>
          <SlidersHorizontal className="size-3.5" />{settings.attention_prompt ? copy.attention_title : copy.customize}
        </Button>
        {settings.proactivity_instructions && <span className="text-[11px] text-muted-foreground">{copy.customized}</span>}
      </div>
    </div>
    {editing && <ProactivityInstructionsDialog projectId={projectId} assignmentId={assignment.id}
      agentName={assignment.agent.name} settings={editing} copy={copy} onClose={() => setEditing(null)} />}
  </div>
}
